import React, { useState, useEffect, useRef } from 'react';
import './TestList.css';
import { ShimmerTableRow, ShimmerTableHeader } from '../Shimmer';
import CustomDropdown from '../CustomDropdown';
import ConfirmModal from '../ConfirmModal';
import { API_BASE_URL } from '../../utils/api';

function TestList({ onSelectTest, onCreateTest, ws, isConnected, sendMessage, onRunTest, selectedApplication }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [dbTests, setDbTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
  const [showMessage, setShowMessage] = useState({ show: false, message: '', type: 'success' });
  const [showTestRunModal, setShowTestRunModal] = useState(false);
  const [runningTest, setRunningTest] = useState(null);
  const [testOutput, setTestOutput] = useState([]);
  const [testStatus, setTestStatus] = useState(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [selectedTestCode, setSelectedTestCode] = useState(null);
  const [selectedTestForCode, setSelectedTestForCode] = useState(null);
  const [showMoveToSuiteModal, setShowMoveToSuiteModal] = useState(false);
  const [renameModal, setRenameModal] = useState({ isOpen: false, test: null, value: '', saving: false, error: '' });
  const [testSuites, setTestSuites] = useState([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState('');
  const [isMovingTests, setIsMovingTests] = useState(false);
  const [overviewStats, setOverviewStats] = useState({
    totalCases: 0,
    passed: 0,
    failed: 0,
    automated: 0
  });
  const [testSuitesMap, setTestSuitesMap] = useState({});
  const [testRunsMap, setTestRunsMap] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [priorityFilter, setPriorityFilter] = useState('All Priority');
  const messageHandlerRef = useRef(null);

  const pageSize = 10;

  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    const lacksTimezone = typeof dateStr === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr);
    const normalized = lacksTimezone ? dateStr.replace(' ', 'T') + 'Z' : dateStr;
    return new Date(normalized);
  };

  const formatTimeAgo = (dateStr) => {
    const date = normalizeDate(dateStr);
    if (!date || isNaN(date.getTime())) return 'N/A';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${Math.floor(diffHours / 24)} days ago`;
  };

  useEffect(() => {
    if (selectedApplication) {
      loadTestCases(currentPage);
      loadTestSuites();
      loadTestRuns();
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedApplication]);

  // Load test suites when move modal is opened
  useEffect(() => {
    if (showMoveToSuiteModal) {
      loadTestSuites();
    }
    // Deliberate: the load* helpers are rebuilt on every render, so listing
    // them here would fetch -> setState -> render -> fetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMoveToSuiteModal]);

  const loadTestRuns = async () => {
    try {
      if (!selectedApplication) {
        setTestRunsMap({});
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/test-runs?limit=1000&application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        const runs = data.testRuns || [];
        const runsMap = {};
        runs.forEach(run => {
          const testId = run.test_case_id;
          const runDate = normalizeDate(run.completed_at || run.started_at);
          if (!testId || !runDate || isNaN(runDate.getTime())) return;

          const existing = runsMap[testId];
          const existingDate = existing ? normalizeDate(existing.completed_at || existing.started_at) : null;

          if (!existingDate || runDate > existingDate) runsMap[testId] = run;
        });
        setTestRunsMap(runsMap);
      }
    } catch (error) {
      console.error('Error loading test runs:', error);
    }
  };

  const loadTestSuites = async () => {
    try {
      if (!selectedApplication) {
        setTestSuites([]);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/test-suites?application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        const suites = data.testSuites || [];
        setTestSuites(suites);
        // Create a map of test case IDs to suite IDs
        const suiteMap = {};
        for (const suite of suites) {
          try {
            const suiteResponse = await fetch(`${API_BASE_URL}/api/test-suites/${suite.id}/test-cases`);
            const suiteData = await suiteResponse.json();
            if (suiteData.success && suiteData.testCases) {
              suiteData.testCases.forEach(tc => {
                suiteMap[tc.id] = suite.id;
              });
            }
          } catch (e) {
            // Ignore errors for individual suite lookups
          }
        }
        setTestSuitesMap(suiteMap);
      }
    } catch (error) {
      console.error('Error loading test suites:', error);
      setTestSuites([]);
    }
  };

  const loadStatistics = async () => {
    try {
      if (!selectedApplication) {
        setOverviewStats({ totalCases: 0, passed: 0, failed: 0, automated: 0 });
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/statistics/test-cases?application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success && data.stats) {
        setOverviewStats({
          totalCases: data.stats.totalCases || 0,
          passed: data.stats.passed || 0,
          failed: data.stats.failed || 0,
          automated: data.stats.automated || 0
        });
      } else {
        setOverviewStats({ totalCases: 0, passed: 0, failed: 0, automated: 0 });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      setOverviewStats({ totalCases: 0, passed: 0, failed: 0, automated: 0 });
    }
  };

  const loadTestCases = async (page) => {
    setLoading(true);
    const startTime = Date.now();
    const minLoadingTime = 2000; // 2 seconds minimum
    
    try {
      // Add cache-busting timestamp to ensure fresh data
      if (!selectedApplication) {
        setDbTests([]);
        setLoading(false);
        return;
      }
      
      console.log('[TESTLIST] Loading test cases', {
        apiBase: API_BASE_URL,
        applicationId: selectedApplication.id,
        page,
        pageSize
      });
      
      const response = await fetch(`${API_BASE_URL}/api/test-cases?page=${page}&limit=${pageSize}&application_id=${selectedApplication.id}&_t=${Date.now()}`);
      if (!response.ok) {
        console.warn('[TESTLIST] Fetch test-cases non-200', { status: response.status, statusText: response.statusText });
      }
      const data = await response.json();
      if (data.success) {
        // Transform database format to frontend format
        const formattedTests = (data.testCases || []).map(test => ({
          id: test.id,
          dbId: test.id,
          test_case_id: test.id,
          test_case_number: test.test_case_number, // the displayed TC-001 code
          suite_names: test.suite_names,           // which suite(s) this test is in
          priority: test.priority,                 // stored, no longer invented in the browser
          last_run_status: test.last_run_status,   // includes runs made as part of a suite
          last_run_at: test.last_run_at,
          last_run_via_suite: test.last_run_via_suite,
          name: test.name,
          version: 'v1',
          creator: 'System',
          creationDate: (normalizeDate(test.created_at) || new Date()).toLocaleString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }).replace(',', ' |'),
          recentRuns: 0,
          environment: 'Production',
          url: test.url || '',
          stepCount: test.actual_step_count || test.step_count || 0,
          content: test.content,
          filename: test.filename
        }));
        setDbTests(formattedTests);
        if (data.pagination) {
          setPagination(data.pagination);
        } else {
          setPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });
        }
      }
    } catch (error) {
      console.error('Error loading test cases:', error);
      setDbTests([]);
      setPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });
    } finally {
      // Ensure minimum 2 seconds loading time
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  };

  // Always use database tests - no localStorage
  const tests = dbTests;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Download the current application's test cases as a spreadsheet. Typed
  // values are masked server-side - the stored step text really does contain
  // the pre-prod password, and a CSV travels further than the tool does.
  const handleExportCsv = () => {
    if (!selectedApplication) return;

    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Export test cases',
      confirmText: 'Download',
      message: `Download all test cases and steps for "${selectedApplication.name}" as a CSV file?\n\nValues typed during recording (passwords, OTPs, email addresses) are masked in the export.`,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
        const url = `${API_BASE_URL}/api/export/test-cases.csv?application_id=${encodeURIComponent(selectedApplication.id)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      },
    });
  };

  const handleCreateTest = () => {
    onCreateTest();
  };

  // Superseded by the onRunTest prop from App; kept as the local fallback.
  // eslint-disable-next-line no-unused-vars
  const handleRunTest = (test) => {
    if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
      setShowMessage({ show: true, message: 'Not connected to backend. Please refresh the page.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    // Ensure test has all required fields for execution
    const testToRun = {
      ...test,
      id: test.id || test.dbId || test.test_case_id,
      dbId: test.id || test.dbId || test.test_case_id,
      filename: test.filename || `tests/${test.name}.spec.ts`,
      url: test.url || ''
    };

    // Navigate to test view with execution mode
    if (onRunTest) {
      onRunTest(testToRun);
    } else {
      // Fallback to select test
      onSelectTest(testToRun);
    }
  };

  // Listen for test saved and test execution completion messages to refresh the list
  useEffect(() => {
    if (!ws || !isConnected) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'testSaved' || data.type === 'testExecutionCompleted' || data.type === 'testFinished') {
          // Refresh the test list when a test is saved or execution completes
          console.log('Test event received, refreshing list...', data.type);
          // Small delay to ensure database is updated
          setTimeout(() => {
            if (selectedApplication) {
              loadTestCases(currentPage);
              loadTestRuns(); // Also refresh test runs to update status
              loadStatistics(); // Refresh statistics
            }
          }, 1000); // Increased delay to ensure test run is saved to database
        }
      } catch (error) {
        // Ignore parse errors
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
    // Deliberate: the load* helpers are rebuilt on every render, so listing
    // them here would fetch -> setState -> render -> fetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, isConnected, currentPage]);

  // Cleanup message handler on unmount
  useEffect(() => {
    return () => {
      if (ws && messageHandlerRef.current) {
        ws.removeEventListener('message', messageHandlerRef.current);
        messageHandlerRef.current = null;
      }
    };
  }, [ws]);

  const handleEditTest = (test) => {
    onSelectTest(test);
  };

  // Rename only touches the test case name. The backend also rewrites the
  // test('...') title inside the generated code so the Playwright report
  // does not keep showing the old name.
  const openRenameModal = (test) => {
    setRenameModal({ isOpen: true, test, value: test.name || '', saving: false, error: '' });
  };

  const closeRenameModal = () => {
    setRenameModal(prev => (prev.saving ? prev : { isOpen: false, test: null, value: '', saving: false, error: '' }));
  };

  const submitRename = async () => {
    const test = renameModal.test;
    if (!test) return;

    const newName = (renameModal.value || '').trim();
    if (!newName) {
      setRenameModal(prev => ({ ...prev, error: 'Test case name cannot be empty' }));
      return;
    }
    if (newName === test.name) {
      closeRenameModal();
      return;
    }

    setRenameModal(prev => ({ ...prev, saving: true, error: '' }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-cases/${test.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      });
      const data = await response.json();

      if (data.success) {
        // Update the row in place - a full reload would replay the 2s shimmer
        // for a change we already know the result of.
        setDbTests(prev => prev.map(t => (
          t.id === test.id
            ? { ...t, name: data.testCase?.name || newName, content: data.testCase?.content ?? t.content }
            : t
        )));
        setRenameModal({ isOpen: false, test: null, value: '', saving: false, error: '' });
        setShowMessage({ show: true, message: 'Test case renamed successfully', type: 'success' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      } else {
        setRenameModal(prev => ({ ...prev, saving: false, error: data.error || 'Failed to rename test case' }));
      }
    } catch (error) {
      setRenameModal(prev => ({ ...prev, saving: false, error: `Error renaming test case: ${error.message}` }));
    }
  };

  const handleViewCode = (test) => {
    // Show the generated test code in a modal
    setSelectedTestCode(test.content || 'No test code available');
    setSelectedTestForCode(test); // Store the test object for update
    setShowCodeModal(true);
  };

  const handleUpdateCode = async () => {
    if (!selectedTestForCode) {
      setShowMessage({ show: true, message: 'No test selected', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    // Get the test case ID - try multiple possible fields
    let testCaseId = selectedTestForCode.id || selectedTestForCode.dbId || selectedTestForCode.test_case_id;
    
    // Convert to number if it's a string number
    if (testCaseId && typeof testCaseId === 'string' && !isNaN(testCaseId)) {
      testCaseId = parseInt(testCaseId, 10);
    }
    
    if (!testCaseId) {
      console.error('Test case ID not found. Test object:', selectedTestForCode);
      setShowMessage({ show: true, message: 'Test case ID not found. Please refresh and try again.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    if (!selectedTestCode || !selectedTestCode.trim()) {
      setShowMessage({ show: true, message: 'Code cannot be empty', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    try {
      console.log('Updating test code for test case ID:', testCaseId);
      const response = await fetch(`${API_BASE_URL}/api/test-cases/${testCaseId}/code`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: selectedTestCode
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowMessage({ show: true, message: 'Test code updated successfully!', type: 'success' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
        
        // Refresh the test list to show updated content
        loadTestCases(currentPage);
        
        // Close the modal
        setShowCodeModal(false);
        setSelectedTestCode(null);
        setSelectedTestForCode(null);
      } else {
        console.error('Update failed:', data);
        setShowMessage({ show: true, message: data.error || 'Failed to update test code', type: 'error' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      }
    } catch (error) {
      console.error('Error updating test code:', error);
      setShowMessage({ show: true, message: `Error updating test code: ${error.message}`, type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
    }
  };

  const handleSelectTest = (testId, checked) => {
    setSelectedTests(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(testId);
      } else {
        newSet.delete(testId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedTests(new Set(tests.map(t => t.id || t.dbId || t.test_case_id)));
    } else {
      setSelectedTests(new Set());
    }
  };

  const handleBulkDelete = () => {
    if (selectedTests.size === 0) {
      setShowMessage({ show: true, message: 'Please select at least one test to delete', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }
    const count = selectedTests.size;
    setConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete ${count} test case(s)? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
        await performBulkDelete(Array.from(selectedTests));
      }
    });
  };

  const performBulkDelete = async (ids, force = false) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-cases/delete-multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids, force }),
      });

      const data = await response.json();

      // One or more of these test cases is used by scheduled tasks.
      if (data.requiresConfirmation) {
        setConfirmModal({
          isOpen: true,
          type: 'danger',
          message: `${data.error} Delete them anyway?`,
          onConfirm: async () => {
            setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
            await performBulkDelete(ids, true);
          }
        });
        return;
      }

      if (data.success) {
        setSelectedTests(new Set());
        loadTestCases(currentPage);
        loadStatistics();
        setShowMessage({ show: true, message: `${data.deletedCount} test case(s) deleted successfully`, type: 'success' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      } else {
        setShowMessage({ show: true, message: 'Error deleting test cases: ' + data.error, type: 'error' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      }
    } catch (error) {
      console.error('Error deleting tests:', error);
      setShowMessage({ show: true, message: 'Error deleting test cases', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
    }
  };

  const handleMoveToSuite = () => {
    if (selectedTests.size === 0) {
      setShowMessage({ show: true, message: 'Please select at least one test to move', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }
    setShowMoveToSuiteModal(true);
    setSelectedSuiteId('');
  };

  const confirmMoveToSuite = async () => {
    if (!selectedSuiteId) {
      setShowMessage({ show: true, message: 'Please select a test suite', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    setIsMovingTests(true);
    const testIds = Array.from(selectedTests);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Move each test case to the suite
      const applicationIdParam = selectedApplication?.id ? `?application_id=${selectedApplication.id}` : '';
      for (const testId of testIds) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/test-suites/${selectedSuiteId}/test-cases${applicationIdParam}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ testCaseId: testId }),
          });

          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            // If test is already in suite, count as success (idempotent)
            if (data.error && data.error.includes('already in this suite')) {
              successCount++;
            } else {
              errorCount++;
            }
          }
        } catch (error) {
          console.error(`Error moving test ${testId}:`, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setShowMessage({ 
          show: true, 
          message: `${successCount} test(s) moved to suite successfully`, 
          type: 'success' 
        });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
        setSelectedTests(new Set());
        setShowMoveToSuiteModal(false);
        setSelectedSuiteId('');
      } else {
        setShowMessage({ 
          show: true, 
          message: `${successCount} test(s) moved, ${errorCount} failed`, 
          type: 'error' 
        });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      }
    } catch (error) {
      console.error('Error moving tests to suite:', error);
      setShowMessage({ show: true, message: 'Error moving tests to suite', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
    } finally {
      setIsMovingTests(false);
    }
  };

  const handleDeleteTest = (testId, e) => {
    e.stopPropagation();
    const test = dbTests.find(t => (t.id || t.dbId || t.test_case_id) === testId);
    const name = test?.name || 'this test case';
    
    setConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
        await performDeleteTest(testId);
      }
    });
  };

  const performDeleteTest = async (testId, force = false) => {
    try {
      // Ensure we're using the correct ID format (convert to string if needed)
      const testIdStr = String(testId);
      const response = await fetch(
        `${API_BASE_URL}/api/test-cases/${testIdStr}${force ? '?force=true' : ''}`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      // The test case is used by scheduled tasks - ask before breaking them.
      if (data.requiresConfirmation) {
        setConfirmModal({
          isOpen: true,
          type: 'danger',
          message: `${data.error} Delete it anyway?`,
          onConfirm: async () => {
            setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
            await performDeleteTest(testId, true);
          }
        });
        return;
      }

      if (data.success) {
        // Force refresh the list
        await loadTestCases(currentPage);
        loadStatistics();
        setShowMessage({ show: true, message: 'Test case deleted successfully', type: 'success' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      } else {
        setShowMessage({ show: true, message: 'Error deleting test case: ' + (data.error || 'Unknown error'), type: 'error' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      setShowMessage({ show: true, message: 'Error deleting test case: ' + error.message, type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
    }
  };


  const getTestStatus = (test) => {
    // The server reports the latest run that INCLUDED this test, whether it ran
    // on its own or as part of a suite. A suite run stores no test_case_id, so
    // the local lookup below missed it and every test read "Pending".
    if (test.last_run_status) {
      return test.last_run_status === 'passed' ? 'Passed'
           : test.last_run_status === 'failed' ? 'Failed' : 'Pending';
    }
    const run = testRunsMap[test.id || test.dbId || test.test_case_id];
    if (run) {
      return run.status === 'passed' ? 'Passed' : run.status === 'failed' ? 'Failed' : 'Pending';
    }
    return 'Pending';
  };

  const getTestPriority = (test) => {
    // Stored on the test case, defaulting to High. It used to be invented here
    // from the row id (id % 3), so it was meaningless and moved if the id did.
    return test.priority || 'High';
  };

  const getTestType = (test) => {
    // If test has content/code, it's automated
    return test.content && test.content.length > 50 ? 'Automated' : 'Manual';
  };

  const getLastRun = (test) => {
    if (test.last_run_at) {
      const formatted = formatTimeAgo(test.last_run_at);
      const when = formatted === 'N/A' ? 'Never' : formatted;
      return test.last_run_via_suite ? `${when} (suite)` : when;
    }
    const run = testRunsMap[test.id || test.dbId || test.test_case_id];
    if (!run) return 'Never';
    const formatted = formatTimeAgo(run.completed_at || run.started_at);
    return formatted === 'N/A' ? 'Never' : formatted;
  };

  const getSuiteId = (test) => {
    // Comes from the server now - suite membership was never fetched, so this
    // always fell through to "-" even for tests that were in a suite.
    return test.suite_names || testSuitesMap[test.id || test.dbId || test.test_case_id] || '-';
  };

  const getTestCode = (test) => {
    // Per-application test case number, assigned once at creation and never
    // reused. Stable enough to quote in a bug report or work item.
    if (test.test_case_number) {
      return `TC-${String(test.test_case_number).padStart(3, '0')}`;
    }
    // Fallback for a row created before numbering existed. The internal id can
    // be a 13-digit timestamp, so show it plainly rather than pretending to pad.
    const testId = test.id || test.dbId || test.test_case_id;
    return `TC-${testId}`;
  };

  // Filter tests based on search and filters
  const filteredTests = tests.filter(test => {
    const matchesSearch = !searchQuery || 
      test.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getTestCode(test).toLowerCase().includes(searchQuery.toLowerCase());
    
    const status = getTestStatus(test);
    const matchesStatus = statusFilter === 'All Status' || status === statusFilter;
    
    const priority = getTestPriority(test);
    const matchesPriority = priorityFilter === 'All Priority' || priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  return (
    <div className="test-list-container">
      {/* Overview Cards */}
      <div className="test-list-overview">
        <div className="overview-card">
          <div className="overview-icon blue">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M6 2V6H14V2H6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 6H16V18H4V6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 10H12M8 14H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="overview-content">
            <div className="overview-label">Total Cases</div>
            <div className="overview-value">{overviewStats.totalCases.toLocaleString()}</div>
          </div>
        </div>

        <div className="overview-card">
          <div className="overview-icon green">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M16 6L8 14L4 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="overview-content">
            <div className="overview-label">Passed</div>
            <div className="overview-value">{overviewStats.passed.toLocaleString()}</div>
          </div>
        </div>

        <div className="overview-card">
          <div className="overview-icon red">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="overview-content">
            <div className="overview-label">Failed</div>
            <div className="overview-value">{overviewStats.failed.toLocaleString()}</div>
          </div>
        </div>

        {/* <div className="overview-card">
          <div className="overview-icon purple">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 2C12.5 2 14.5 4 14.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="overview-content">
            <div className="overview-label">Automated</div>
            <div className="overview-value">{overviewStats.automated.toLocaleString()}</div>
          </div>
        </div> */}
      </div>

      {/* Search and Filter Controls */}
      <div className="test-list-controls">
        <div className="search-container">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search test cases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          <CustomDropdown
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'All Status', label: 'All Status' },
              { value: 'Passed', label: 'Passed' },
              { value: 'Failed', label: 'Failed' },
              { value: 'Pending', label: 'Pending' }
            ]}
            className="filter-select"
          />
          <CustomDropdown
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            options={[
              { value: 'All Priority', label: 'All Priority' },
              { value: 'High', label: 'High' },
              { value: 'Medium', label: 'Medium' },
              { value: 'Low', label: 'Low' }
            ]}
            className="filter-select"
          />
          {/* <button className="btn-filter">Filter</button> */}
          <button
            className="btn-export"
            onClick={handleExportCsv}
            title="Download these test cases and their steps as a spreadsheet"
            disabled={!selectedApplication}
          >
            Export CSV
          </button>
          <button className="btn-new-test-case" onClick={handleCreateTest}>
            + New Test Case
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar - Show when tests are selected */}
      {selectedTests.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <span className="selected-count">{selectedTests.size} test case(s) selected</span>
          </div>
          <div className="bulk-actions-buttons">
            <button
              className="btn-bulk-action btn-add-to-suite"
              onClick={handleMoveToSuite}
              title="Add to Test Suite"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Add to Suite
            </button>
            <button
              className="btn-bulk-action btn-delete-bulk"
              onClick={handleBulkDelete}
              title="Delete Selected"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Delete Selected
            </button>
            <button
              className="btn-bulk-action btn-clear-selection"
              onClick={() => setSelectedTests(new Set())}
              title="Clear Selection"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Clear
            </button>
          </div>
        </div>
      )}

    <div className="test-list-table-container">
      <div className={`test-list-table-wrapper ${loading ? 'shimmer-scroll-hide' : ''}`}>
          <table className="test-list-table">
            <thead>
              {loading ? (
                <ShimmerTableHeader columns={8} />
              ) : (
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={filteredTests.length > 0 && selectedTests.size === filteredTests.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th>TEST CASE</th>
                  <th>SUITE</th>
                  <th>PRIORITY</th>
                  {/* <th>TYPE</th> */}
                  <th>STATUS</th>
                  <th>LAST RUN</th>
                  <th>ACTIONS</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                // Shimmer loading rows
                Array.from({ length: pageSize }).map((_, index) => (
                  <ShimmerTableRow key={`shimmer-${index}`} columns={8} />
                ))
              ) : filteredTests.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                    No test cases found
                  </td>
                </tr>
              ) : (
                // Actual data rows
                filteredTests.map((test) => {
                  const testId = test.id || test.dbId || test.test_case_id;
                  const isSelected = selectedTests.has(testId);
                  const status = getTestStatus(test);
                  const priority = getTestPriority(test);
                  // Feeds the Type column, which is currently commented out below.
                  // eslint-disable-next-line no-unused-vars
                  const testType = getTestType(test);
                  const lastRun = getLastRun(test);
                  const suiteId = getSuiteId(test);
                  const testCode = getTestCode(test);
                  
                  return (
                  <tr key={testId} className={isSelected ? 'selected' : ''} >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedTests.has(testId)}
                        onChange={(e) => handleSelectTest(testId, e.target.checked)}
                      />
                    </td>
                    <td>
                      <div className="test-case-info">
                        <div className="test-case-name">{testCode}: {test.name}</div>
                        {test.stepCount > 0 && (
                          <div className="test-case-meta">
                            {test.stepCount} {test.stepCount === 1 ? 'step' : 'steps'}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{suiteId}</td>
                    <td>
                      <span className={`priority-badge ${priority.toLowerCase()}`}>
                        {priority}
                      </span>
                    </td>
                    {/* <td>
                      <span className={`type-badge ${testType.toLowerCase()}`}>
                        {testType}
                      </span>
                    </td> */}
                    <td>
                      <span className={`status-badge ${status.toLowerCase()}`}>
                        {status === 'Passed' ? '●' : status === 'Failed' ? '●' : '○'} {status}
                      </span>
                    </td>
                    <td>{lastRun}</td>
                    <td>
                      <div className="test-actions">
                        <button
                          className="action-btn"
                          onClick={() => handleViewCode(test)}
                          title="View Code"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2H14V14H2V2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            <path d="M5 5H11M5 8H11M5 11H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => openRenameModal(test)}
                          title="Rename test case"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8.4 1.6H14.4V7.6L7.6 14.4L1.6 8.4L8.4 1.6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            <circle cx="11.3" cy="4.7" r="1" fill="currentColor"/>
                          </svg>
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleEditTest(test)}
                          title="Edit steps"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.333 2.667C11.5084 2.49164 11.7163 2.35259 11.9441 2.25781C12.1719 2.16303 12.4151 2.11438 12.6663 2.11438C12.9176 2.11438 13.1608 2.16303 13.3886 2.25781C13.6164 2.35259 13.8243 2.49164 13.9997 2.667C14.175 2.84236 14.3141 3.05025 14.4089 3.27805C14.5037 3.50585 14.5523 3.74905 14.5523 4.00033C14.5523 4.25161 14.5037 4.49481 14.4089 4.72261C14.3141 4.95041 14.175 5.1583 13.9997 5.33366L5.33301 14.0003L1.33301 15.3337L2.66634 11.3337L11.333 2.667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          </svg>
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={(e) => handleDeleteTest(testId, e)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination - Outside table container */}
      {!loading && (pagination.total > 10 || filteredTests.length > 10) && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total || filteredTests.length)} of {pagination.total || filteredTests.length} test cases
          </span>
          {pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                &lt;
              </button>
              {Array.from({ length: Math.min(3, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                );
              })}
              {pagination.totalPages > 3 && (
                <span style={{ padding: '0 0.5rem', color: '#6B7280' }}>...</span>
              )}
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        title={confirmModal.title || 'Confirm Delete'}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' })}
        confirmText={confirmModal.confirmText || 'Delete'}
        cancelText="Cancel"
        type={confirmModal.type}
      />

      {/* Test Run Modal */}
      {showTestRunModal && runningTest && (
        <div className="modal-overlay" onClick={() => { 
          // Clean up message handler
          if (ws && messageHandlerRef.current) {
            ws.removeEventListener('message', messageHandlerRef.current);
            messageHandlerRef.current = null;
          }
          setShowTestRunModal(false); 
          setRunningTest(null); 
          setTestOutput([]); 
          setTestStatus(null);
        }}>
          <div className="modal-content test-run-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Running Test: {runningTest.name}</h3>
              <button className="modal-close" onClick={() => { 
                if (ws && messageHandlerRef.current) {
                  ws.removeEventListener('message', messageHandlerRef.current);
                  messageHandlerRef.current = null;
                }
                setShowTestRunModal(false); 
                setRunningTest(null); 
                setTestOutput([]); 
                setTestStatus(null);
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="test-run-status">
                {testStatus === 'success' && <span className="status-badge success">✓ Passed</span>}
                {testStatus === 'failed' && <span className="status-badge failed">✗ Failed</span>}
                {testStatus === 'error' && <span className="status-badge error">⚠ Error</span>}
                {testStatus === null && <span className="status-badge running">⟳ Running...</span>}
              </div>
              <div className="test-run-output">
                {testOutput.length === 0 && testStatus === null && (
                  <div className="test-output-line info">Starting test execution...</div>
                )}
                {testOutput.map((output, index) => (
                  <div key={index} className={`test-output-line ${output.type}`}>
                    {output.message}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => { 
                  // Clean up message handler
                  if (ws && messageHandlerRef.current) {
                    ws.removeEventListener('message', messageHandlerRef.current);
                    messageHandlerRef.current = null;
                  }
                  setShowTestRunModal(false); 
                  setRunningTest(null); 
                  setTestOutput([]); 
                  setTestStatus(null);
                }}
              >
                {testStatus ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Code Modal */}
      {showCodeModal && (
        <div className="modal-overlay" onClick={() => { setShowCodeModal(false); setSelectedTestCode(null); setSelectedTestForCode(null); }}>
          <div className="modal-content code-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h3>Test Code {selectedTestForCode?.name ? `- ${selectedTestForCode.name}` : ''}</h3>
              <button 
                className="modal-close" 
                onClick={() => { setShowCodeModal(false); setSelectedTestCode(null); setSelectedTestForCode(null); }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <textarea
                className="code-textarea"
                value={selectedTestCode || ''}
                onChange={(e) => setSelectedTestCode(e.target.value)}
                placeholder="No test code available"
              />
            </div>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  navigator.clipboard.writeText(selectedTestCode || '');
                  setShowMessage({ show: true, message: 'Code copied to clipboard!', type: 'success' });
                  setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 2000);
                }}
              >
                Copy Code
              </button>
              <button 
                className="btn-primary" 
                onClick={handleUpdateCode}
                disabled={!selectedTestForCode || !selectedTestCode || !selectedTestCode.trim()}
              >
                Update Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move to Suite Modal */}
      {showMoveToSuiteModal && (
        <div className="modal-overlay" onClick={() => { 
          if (!isMovingTests) {
            setShowMoveToSuiteModal(false); 
            setSelectedSuiteId('');
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Move Tests to Suite</h3>
              <button className="modal-close" onClick={() => { 
                if (!isMovingTests) {
                  setShowMoveToSuiteModal(false); 
                  setSelectedSuiteId('');
                }
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>Select a test suite to move {selectedTests.size} selected test(s) to:</p>
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
                  Test Suite:
                </label>
                <CustomDropdown
                  value={selectedSuiteId}
                  onChange={(e) => setSelectedSuiteId(e.target.value)}
                  disabled={isMovingTests}
                  options={[
                    { value: '', label: '-- Select a test suite --' },
                    ...testSuites.map((suite) => ({
                      value: suite.id.toString(),
                      label: `${suite.name}${suite.test_case_count > 0 ? ` (${suite.test_case_count} tests)` : ''}`
                    }))
                  ]}
                  className="filter-select"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => { 
                  if (!isMovingTests) {
                    setShowMoveToSuiteModal(false); 
                    setSelectedSuiteId('');
                  }
                }}
                disabled={isMovingTests}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm" 
                onClick={confirmMoveToSuite}
                disabled={!selectedSuiteId || isMovingTests}
              >
                {isMovingTests ? 'Moving...' : 'Move Tests'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Test Case Modal */}
      {renameModal.isOpen && (
        <div className="modal-overlay" onClick={closeRenameModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rename Test Case</h3>
              <button className="modal-close" onClick={closeRenameModal}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
                  Test case name:
                </label>
                <input
                  type="text"
                  value={renameModal.value}
                  onChange={(e) => setRenameModal(prev => ({ ...prev, value: e.target.value, error: '' }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); submitRename(); }
                    if (e.key === 'Escape') { closeRenameModal(); }
                  }}
                  disabled={renameModal.saving}
                  autoFocus
                  placeholder="Enter test case name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${renameModal.error ? '#f44336' : '#E5E7EB'}`,
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                />
                {renameModal.error && (
                  <p style={{ color: '#f44336', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                    {renameModal.error}
                  </p>
                )}
                <p style={{ color: '#6B7280', fontSize: '0.8rem', margin: '0.75rem 0 0' }}>
                  The TC number and the recorded steps stay the same. Only the name changes.
                </p>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeRenameModal} disabled={renameModal.saving}>
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={submitRename}
                disabled={renameModal.saving || !(renameModal.value || '').trim()}
              >
                {renameModal.saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Toast */}
      {showMessage.show && (
        <div className={`message-toast ${showMessage.type}`} style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: showMessage.type === 'success' ? '#4caf50' : '#f44336',
          color: 'white',
          borderRadius: '4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          zIndex: 10000,
          animation: 'slideIn 0.3s ease-out',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          minWidth: '300px',
          maxWidth: '500px'
        }}>
          <span style={{ flex: 1 }}>{showMessage.message}</span>
          <button 
            onClick={() => setShowMessage({ show: false, message: '', type: 'success' })}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1',
              opacity: '0.8',
              marginLeft: '0.5rem'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export default TestList;

